import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import clsx from "clsx";
import { toast } from "react-hot-toast";

import Button from "components/common/Button";
import Divider from "components/common/Divider";
import TextareaInput from "components/common/InputField/TextareaInput";
import TextInput from "components/common/InputField/TextInput";
import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import MultiselectInputV2 from "components/common/InputField/MultiselectInputV2";
import Avatar from "components/common/Avatar";
import SpaceGuestBadge from "./SpaceGuestBadge";
import useToggle from "hooks/useToggle";
import SpaceInviteGuestModal from "./SpaceInviteGuestModal";
import useStepper from "hooks/useStepper";
import { useCreateSpace, useUpdateSpace } from "hooks/spaceHooks";
import { getTextFieldProps } from "helpers/getTextFieldProps";
import CustomToast from "components/common/CustomToast";
import { Space, SpaceMember } from "redux/Ldb/types/spaceTypes";
import useGetTeamMembers2 from "hooks/teamHooks/useGetTeamMembers2";
import { getFromStorage } from "helpers";
import AddTeamMemberModal from "components/views/Synth2.0/TeamMember/AddTeamMemberModal";
import SearchTextField from "components/common/InputField/SearchTextField";
import { useAppSelector } from "hooks/storeHooks";
import { generatePath, useNavigate } from "react-router-dom";
import * as UrlConstants from "constants/urls";

type SpaceAddEditModalProps = { spaceData?: Space } & Omit<ModalContainerProps, "children">;

export default function SpaceAddEditModal(props: SpaceAddEditModalProps) {
    const { closeModal, spaceData, ...rest } = props;
    const [addTeamMemberModal, toggleAddTeamMemberModal] = useToggle(false);
    const userProfile = getFromStorage("ally-user");
    const navigate = useNavigate();
    const isGuest = userProfile?.isGuest;

    const [createSpaceMutation, createSpaceMutationResult] = useCreateSpace();
    const [updateSpaceMutation, updateSpaceMutationResult] = useUpdateSpace();
    const [filterMembers, setFilterMembers] = useState("");

    const { createdSpaceSuccess } = useAppSelector((state) => state.space);

    const getTeamListQuery = useGetTeamMembers2(
        useMemo(
            () => ({
                page: 1,
                limit: 100,
                assignedBrandsId:
                    userProfile?.user?.assignedBrandsId?.[0] || userProfile?.user?.profile?._id,
            }),
            []
        )
    );
    const isEdit = Object.keys(spaceData || {}).length > 0;

    const stepper = useStepper();

    const formik = useFormik({
        initialValues: {
            spaceName: spaceData?.name || "",
            spaceDescription: spaceData?.description || "",
            members:
                spaceData?.members
                    ?.filter((member) => !(member as any)?.guestId)
                    ?.map((member) => {
                        const spaceMember = member as unknown as SpaceMember;
                        return spaceMember?.ldb?._id;
                    }) || [],
            guests: [],
        },
        enableReinitialize: true,
        validationSchema: yup.object({
            spaceName: yup.string().required("Space name is required"),
            spaceDescription: yup.string().required("Space description is required"),
            members: yup.array().of(yup.string()),
            guests: yup.array().of(yup.string().email("Invalid email address")),
        }),
        onSubmit: async (values) => {
            try {
                if (isEdit) {
                    await updateSpaceMutation(spaceData?._id, {
                        name: values.spaceName,
                        description: values.spaceDescription,
                    });
                } else {
                    await createSpaceMutation({
                        name: values.spaceName,
                        description: values.spaceDescription,
                        memberIds: values.members,
                        guestEmails: values.guests,
                    });
                }

                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message={`${
                            isEdit ? "Space updated successfully" : "Space created successfully"
                        }`}
                        type="success"
                    />
                ));
                handleAddEditSpaceCloseModal();
            } catch (error) {
                toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
            }
        },
    });

    const handleAddEditSpaceCloseModal = () => {
        closeModal?.();
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterMembers(e.target.value);
    };

    const filteredMembersList = useMemo(() => {
        return getTeamListQuery?.data?.filter(
            (member) =>
                member?.fullName?.toLowerCase()?.includes(filterMembers.toLowerCase()) ||
                member?.email?.toLowerCase()?.includes(filterMembers.toLowerCase())
        );
    }, [filterMembers, getTeamListQuery?.data]);

    useEffect(() => {
        if (Boolean(createdSpaceSuccess)) {
            if (isGuest) {
                navigate(
                    generatePath(UrlConstants.SPACES_GUEST_DETAILS, {
                        spaceId: createdSpaceSuccess?.data?.space?._id,
                    })
                );
            } else {
                navigate(
                    generatePath(UrlConstants.SPACES_DETAILS, {
                        spaceId: createdSpaceSuccess?.data?.space?._id,
                    })
                );
            }
        }
    }, [createdSpaceSuccess]);

    return (
        <>
            {
                [
                    <ModalContainer
                        tailwindClassName="w-full max-w-[500px] max-h-[90vh]"
                        closeModal={() => {
                            handleAddEditSpaceCloseModal();
                        }}
                        {...rest}
                    >
                        <form onSubmit={formik.handleSubmit} className="w-full bg-white rounded-lg">
                            <div className="px-[24px] flex justify-between py-[12px] items-center ">
                                <p className="text-lg font-rocGroteskMedium text-g-75">
                                    {isEdit ? "Edit Space" : "Create a Space"}
                                </p>
                                <i
                                    className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                                    onClick={() => {
                                        handleAddEditSpaceCloseModal();
                                    }}
                                ></i>
                            </div>

                            <Divider />

                            <div className="p-[24px] space-y-4 overflow-y-scroll max-h-[350px] h-full scrollbar-hidden">
                                <TextInput
                                    disableNativeValidation
                                    {...getTextFieldProps(formik, "spaceName")}
                                    name="spaceName"
                                    placeholder="Enter a space name"
                                />
                                <TextareaInput
                                    {...getTextFieldProps(formik, "spaceDescription")}
                                    name="spaceDescription"
                                    placeholder="Description"
                                    disableNativeValidation
                                />

                                {!isEdit && (
                                    <>
                                        {" "}
                                        <MultiselectInputV2
                                            {...formik.getFieldProps("members")}
                                            name="members"
                                            value={formik.values.members}
                                            handleChange={(name, value, selectedItems) => {
                                                formik.setFieldValue(name, selectedItems);
                                            }}
                                            inputValueContainer={
                                                <p className="text-base whitespace-nowrap truncate scrollbar-hidden overflow-hidden font-rocGroteskMedium ">
                                                    {formik.values.members?.length > 0
                                                        ? formik.values.members
                                                              .map(
                                                                  (member) =>
                                                                      getTeamListQuery?.data?.find(
                                                                          (team) =>
                                                                              team._id === member
                                                                      )?.email || member
                                                              )
                                                              .join(", ")
                                                        : ""}
                                                </p>
                                            }
                                            customTopDropdownContainer={
                                                <SearchTextField
                                                    inputContainerClassname={`!border-t-0 !border-l-0 !border-r-0 !rounded-b-none`}
                                                    inputClassName={`!rounded-b-none !rounded-md`}
                                                    value={filterMembers}
                                                    inputPlaceholder={"Search"}
                                                    onChange={handleFilterChange}
                                                />
                                            }
                                            placeholder="Select team members"
                                            customFilterItem={(option) => {
                                                const teamMember = getTeamListQuery?.data?.find(
                                                    (member) => member.email === option.subText
                                                );
                                                const isSynth =
                                                    teamMember?.name?.toLocaleLowerCase() ===
                                                    "synth";
                                                return (
                                                    <div className="flex items-center gap-2 px-[12px] py-2">
                                                        <Avatar
                                                            src={teamMember?.profile?.avatar}
                                                            alt={teamMember?.fullName}
                                                        />
                                                        <div>
                                                            <p
                                                                className={clsx(
                                                                    isSynth && "text-[#62CC00]",
                                                                    "text-sm font-rocGroteskMedium"
                                                                )}
                                                            >
                                                                {teamMember?.fullName ||
                                                                    teamMember?.email ||
                                                                    "N/A"}
                                                            </p>
                                                            <p
                                                                className={clsx(
                                                                    "text-sm !text-slate-400 font-rocGroteskMedium"
                                                                )}
                                                            >
                                                                {teamMember?.role
                                                                    ?.split("_")
                                                                    .join(" ") ||
                                                                    teamMember?.email ||
                                                                    "N/A"}
                                                            </p>
                                                        </div>

                                                        <div className="flex-1" />

                                                        {teamMember?.isGuest && (
                                                            <SpaceGuestBadge className="bg-[#DCFCE7] text-[#31BC34]" />
                                                        )}
                                                    </div>
                                                );
                                            }}
                                            dropdownOptions={filteredMembersList?.map((member) => ({
                                                label: member?.fullName || "N/A",
                                                subText: member?.email,
                                                value: member._id,
                                            }))}
                                            topCustomDropdownContainer={
                                                <div
                                                    className="flex items-center gap-2 px-[12px] py-2 block cursor-pointer border-b border-slate-200"
                                                    onClick={() => {
                                                        toggleAddTeamMemberModal();
                                                    }}
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex justify-center items-center border-dashed border-slate-400 border">
                                                        <i className="ri-add-line text-[18px] text-slate-400"></i>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-rocGroteskMedium">
                                                            Invite new team member
                                                        </p>
                                                        <p className="text-sm !text-slate-400 font-rocGroteskMedium">
                                                            Enter email addresses of team member not
                                                            on Synth yet
                                                        </p>
                                                    </div>
                                                </div>
                                            }
                                        />
                                        <div>
                                            <TextInput
                                                name="guests"
                                                placeholder="Guest Enter email addresses"
                                                {...getTextFieldProps(formik, "guests")}
                                                onChange={(e) => {
                                                    const guests = e.target.value.split(",");
                                                    formik.setFieldValue("guests", guests);
                                                }}
                                                value={formik.values.guests.join(",")}
                                            />
                                            <p className="text-sm text-slate-400 font-rocGroteskMedium">
                                                Use comma to separate email addresses
                                            </p>
                                        </div>
                                    </>
                                )}

                                {isEdit && (
                                    <Button
                                        btnText="Invite Guest"
                                        icon={<i className="ri-add-line text-[18px]"></i>}
                                        onClick={() => {
                                            stepper.next();
                                        }}
                                        btnClassname="!w-fit !px-0 !py-0 !text-p-50 !bg-transparent !text-sm"
                                    />
                                )}
                            </div>

                            <Divider />

                            <div className="px-[24px] flex justify-end gap-2 py-[16px] items-center ">
                                <Button
                                    btnText="Cancel"
                                    onClick={() => {
                                        handleAddEditSpaceCloseModal();
                                    }}
                                    btnClassname="!bg-n-20 !w-fit"
                                />
                                <Button
                                    btnText={isEdit ? "Update Space" : "Create Space"}
                                    isLoading={
                                        createSpaceMutationResult.isLoading ||
                                        updateSpaceMutationResult.isLoading
                                    }
                                    type="submit"
                                    btnClassname="!w-fit"
                                />
                            </div>
                        </form>
                    </ModalContainer>,

                    <SpaceInviteGuestModal
                        handleBack={() => {
                            stepper.previous();
                        }}
                        closeModal={handleAddEditSpaceCloseModal}
                        {...rest}
                    />,
                ][stepper.step]
            }

            <AddTeamMemberModal
                addNewMember={addTeamMemberModal}
                setAddNewMember={toggleAddTeamMemberModal}
                variant="invite"
            />
        </>
    );
}
